/**
 * LeadForge France - Header Component
 */
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Database, 
  User, 
  LogOut, 
  Settings, 
  ShoppingBag,
  Menu,
  X
} from 'lucide-react';

const Header = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-[#E2E8F0]">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-[#0F172A] rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-[#0F172A] hidden sm:block">
              LeadForge France
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link
              to="/purchase"
              className={`text-sm font-medium transition-colors ${
                isActive('/categories') ? 'text-[#2563EB]' : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
              data-testid="nav-categories"
            >
              Catégories
            </Link>
            <Link
              to="/pricing"
              className={`text-sm font-medium transition-colors ${
                isActive('/pricing') ? 'text-[#2563EB]' : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
              data-testid="nav-pricing"
            >
              Tarifs
            </Link>
            <Link
              to="/creation-site-web"
              className={`text-sm font-medium transition-colors ${isActive('/creation-site-web') ? 'text-[#2563EB]' : 'text-[#64748B] hover:text-[#0F172A]'}`}
              data-testid="nav-creation-site"
            >
              Création de site
            </Link>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/purchase" className="hidden sm:block">
                  <Button className="btn-cta text-sm py-2 px-4" data-testid="buy-leads-btn">
                    Acheter des leads
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                      <div className="w-8 h-8 bg-[#F1F5F9] rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-[#64748B]" />
                      </div>
                      <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="menu-dashboard">
                        <ShoppingBag className="w-4 h-4" />
                        Mes commandes
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center gap-2" data-testid="menu-settings">
                        <Settings className="w-4 h-4" />
                        Paramètres
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2" data-testid="menu-admin">
                            <Database className="w-4 h-4" />
                            Administration
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-sm" data-testid="login-btn">
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E2E8F0]">
            <nav className="flex flex-col gap-2">
              <Link
                to="/purchase"
                className="px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-[#F1F5F9] rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Catégories
              </Link>
              <Link
                to="/pricing"
                className="px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-[#F1F5F9] rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Tarifs
              </Link>
              <Link
                to="/creation-site-web"
                className="px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-[#F1F5F9] rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Création de site
              </Link>
              {isAuthenticated && (
                <Link
                  to="/purchase"
                  className="px-4 py-2 text-sm font-medium text-[#2563EB] hover:bg-[#F1F5F9] rounded-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Acheter des leads
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
